import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AutoAwesomeRounded, TimelineRounded } from "@mui/icons-material";
import { createUser } from "../firebase/userService";

const UserOnboarding = ({ onComplete }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    if (!name.trim()) {
      setError("Please enter your name to continue.");
      return;
    }

    try {
      setLoading(true);
      const userId = await createUser(name.trim());
      localStorage.setItem("userName", name.trim());
      onComplete(userId);
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 4,
      }}
    >
      <Box
        sx={{
          width: "min(100%, 1080px)",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            p: { xs: 3.2, md: 5 },
            borderRadius: 8,
            background:
              "linear-gradient(160deg, rgba(15,23,42,0.97), rgba(15,118,110,0.86))",
            color: "white",
            boxShadow: "0 36px 90px rgba(15, 23, 42, 0.22)",
          }}
        >
          <Chip
            label="Premium onboarding"
            sx={{
              mb: 2.5,
              bgcolor: "rgba(255,255,255,0.12)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.16)",
            }}
          />
          <Typography variant="h1" sx={{ mb: 2, maxWidth: 480 }}>
            Build a calm, reliable timeline for your life.
          </Typography>
          <Typography variant="subtitle1" sx={{ color: "rgba(255,255,255,0.74)", mb: 3 }}>
            Track exams, health visits, legal deadlines, and recurring moments in one
            polished workspace built to reduce mental load.
          </Typography>

          <Stack spacing={2}>
            {[
              "Focused dashboard for upcoming priorities",
              "Recurring celebrations and yearly milestones",
              "Responsive experience across phone and desktop",
            ].map((item) => (
              <Stack key={item} direction="row" spacing={1.25} alignItems="center">
                <AutoAwesomeRounded sx={{ color: "#7dd3fc" }} />
                <Typography>{item}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            p: { xs: 3, md: 4.5 },
            borderRadius: 8,
            background:
              "radial-gradient(circle at top right, rgba(96,165,250,0.18), transparent 30%), linear-gradient(180deg, rgba(255,255,255,0.97), rgba(248,250,252,0.94))",
            border: "1px solid rgba(255,255,255,0.92)",
            boxShadow: "0 28px 72px rgba(15, 23, 42, 0.10)",
          }}
        >
          <Stack spacing={2.25}>
            <Box
              sx={{
                width: 56,
                height: 56,
                display: "grid",
                placeItems: "center",
                borderRadius: 3.5,
                bgcolor: "primary.main",
                color: "white",
                boxShadow: "0 16px 34px rgba(37, 99, 235, 0.26)",
              }}
            >
              <TimelineRounded />
            </Box>

            <Box>
              <Typography variant="h3" sx={{ mb: 1 }}>
                Welcome to VenTush
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Personalize your workspace with a name. We&apos;ll use it to keep the
                experience warm and human.
              </Typography>
            </Box>

            <TextField
              label="Your name"
              placeholder="What should we call you?"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              autoFocus
              fullWidth
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} sx={{ color: "white" }} /> : null}
            >
              {loading ? "Setting up your workspace..." : "Enter VenTush"}
            </Button>

            <Typography variant="body2" color="text.secondary">
              Your profile stays lightweight and only powers your own timeline data.
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default UserOnboarding;
